import { useEffect, useState } from "react";
import { FormattedMessage as FM } from "react-intl";

import { PlusIcon } from "components/Icons";
import ListDeleteConfirm from "./ListDeleteConfirm";
import ListError from "./ListError";
import ListItemCard from "./ListItemCard";
import ListItemForm from "./ListItemForm";
import {
  deleteItem,
  fetchItems,
  getDefaults,
  getDetail,
  saveItem,
  toForm,
  validateForm,
} from "../helpers/listEditor";
import {
  NEW_ITEM,
  STYLE_ACTION_BUTTON,
  STYLE_ACTION_BUTTON_PRIMARY,
} from "../constants";

function ListEditor({ config, parentId }) {
  const [items, setItems] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await fetchItems(config, parentId);
      if (cancelled) return;
      setLoadFailed(Boolean(error));
      if (!error) setItems(data || []);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [config, parentId, reloadKey]);

  function reload() {
    setReloadKey((key) => key + 1);
  }

  function openEditor(id, values) {
    setEditing(id);
    setForm(values);
    setSaved(values);
    setFormError(null);
    setConfirmId(null);
  }

  function closeEditor() {
    setEditing(null);
    setForm(null);
    setSaved(null);
    setFormError(null);
  }

  function handleAdd() {
    openEditor(NEW_ITEM, getDefaults(config.fields));
  }

  function handleEdit(item) {
    openEditor(item.id, toForm(config.fields, item));
  }

  function handleChange(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleAskDelete(id) {
    setDeleteError(null);
    setConfirmId(id);
  }

  async function handleSave() {
    const invalid = validateForm(config.fields, form);
    setFormError(invalid);
    if (invalid) return;
    const isNew = editing === NEW_ITEM;
    setSaving(true);
    const { id, error } = await saveItem({
      config,
      parentId,
      recordId: isNew ? null : editing,
      form,
      saved,
    });
    setSaving(false);
    if (error) {
      setFormError({
        id: "melhous.dash.list.save_error",
        detail: getDetail(error),
      });
      return;
    }
    reload();
    if (isNew && config.children && id) {
      setEditing(id);
      setSaved(form);
    } else {
      closeEditor();
    }
  }

  async function handleDelete() {
    setRemoving(true);
    setDeleteError(null);
    const { error } = await deleteItem(config, confirmId);
    setRemoving(false);
    if (error) {
      setDeleteError({
        id: "melhous.dash.list.delete_error",
        detail: getDetail(error),
      });
      return;
    }
    setConfirmId(null);
    reload();
  }

  const locked = editing !== null || confirmId !== null;
  const editorProps = {
    fields: config.fields,
    form,
    error: formError,
    saving,
    onChange: handleChange,
    onSave: handleSave,
    onCancel: closeEditor,
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {config.title && (
            <h4 className="text-sm font-semibold text-neutral-800">
              <FM id={config.title} />
            </h4>
          )}
          <p className="text-xs text-neutral-500">
            <FM id={config.hint} />
          </p>
        </div>
        <button
          type="button"
          disabled={locked || items === null}
          onClick={handleAdd}
          className={`${STYLE_ACTION_BUTTON_PRIMARY} flex min-h-11 items-center gap-1.5 disabled:opacity-50`}
        >
          <PlusIcon width={16} className="fill-current" />
          <FM id={config.add} />
        </button>
      </div>
      {editing === NEW_ITEM && (
        <ListItemForm {...editorProps}>
          {config.children && (
            <p className="text-xs text-neutral-500">
              <FM id={config.children.blocked} />
            </p>
          )}
        </ListItemForm>
      )}
      {items === null && !loadFailed && (
        <p className="text-sm text-neutral-500">
          <FM id="melhous.dash.list.loading" />
        </p>
      )}
      {loadFailed && (
        <div className="flex flex-wrap items-center gap-2">
          <ListError error={{ id: "melhous.dash.list.load_error" }} />
          <button
            type="button"
            onClick={reload}
            className={`${STYLE_ACTION_BUTTON} min-h-11`}
          >
            <FM id="melhous.dash.list.retry" />
          </button>
        </div>
      )}
      {items?.length === 0 && editing === null && (
        <p className="rounded-md border border-dashed border-neutral-300 px-3 py-6 text-center text-sm text-neutral-500">
          <FM id={config.empty} />
        </p>
      )}
      {items?.length > 0 && (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id}>
              {editing === item.id ? (
                <ListItemForm {...editorProps}>
                  {config.children && (
                    <ListEditor config={config.children} parentId={item.id} />
                  )}
                </ListItemForm>
              ) : (
                <ListItemCard
                  config={config}
                  item={item}
                  locked={locked}
                  onEdit={handleEdit}
                  onAskDelete={handleAskDelete}
                >
                  {confirmId === item.id && (
                    <ListDeleteConfirm
                      item={item}
                      config={config}
                      removing={removing}
                      error={deleteError}
                      onCancel={() => setConfirmId(null)}
                      onConfirm={handleDelete}
                    />
                  )}
                </ListItemCard>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ListEditor;
